<template>
  <div class="index">
    <!-- Initial spinner while fetching projects -->
    <div v-if="isInitialLoading" class="global-spinner-container">
      <div class="spinner"></div>
    </div>

    <div class="projects" :class="{ expanded: isExpanded }" v-else>
      <div
          class="projects-container"
          :class="{ expanded: isExpanded }"
          @keydown.esc="() => isExpanded = false"
          @keydown.left="() => navigate('previous')"
          @keydown.right="() => navigate('next')"
          tabindex="0"
          ref="containerRef"
      >
        <div
            v-for="(project, pIndex) in projectStore.visibleProjects"
            :key="project.id"
            class="project-row"
            :class="[
              projectClasses(pIndex),
              { 'loading': !isProjectReady(project.id) }
            ]"
            :data-project-id="project.id"
        >
          <div class="project-content">
            <ProjectInfo
                :name="project.attributes.name"
                :date="project.attributes.date"
                :description="project.attributes.description"
                @click-header="() => handleHeaderClick(pIndex)"
                :class="infoClasses(pIndex)"
                styleVariant="main"
                :showDescription="true"
                :isExpanded="isExpanded"
            />

            <ImageGrid
                :project="project"
                :projectIndex="pIndex"
                :isExpanded="isExpanded"
                :currentProjectIndex="currentProjectIndex"
                :currentImageIndex="currentImageIndex"
                :projectLoadingStatus="projectLoadingStatus"
                :visibleImages="getProjectVisibleImages(project.id)"
                :getImageUrl="getImageUrl"
                :handleWheel="handleWheel"
                :handleScroll="handleScroll"
                :handleTouchStart="handleTouchStart"
                :handleTouchMove="handleTouchMove"
                :handleTouchEnd="handleTouchEnd"
                :navigate="navigate"
                @navigate="navigate"
                @image-selection="handleImageSelection"
                @swipe-down="handleHeaderClick(pIndex)"
                styleVariant="main"
                ref="imageGridRefs"
            />
          </div>
        </div>

        <span ref="loadMoreTrigger" class="load-more-trigger"></span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onBeforeUnmount, ref, onUnmounted, nextTick, computed, watch, onActivated } from 'vue';
import ProjectInfo from '@/components/projects/ProjectInfo.vue';
import ImageGrid from '@/components/projects/ImageGrid.vue';
import { useGallery } from '@/hooks/useGallery';
import { useSharedThumbnails } from '@/hooks/useSharedThumbnails';
import { useProjectStore } from '@/stores/projects';

const props = defineProps({
  projects: {
    type: Array,
    default: () => []
  }
});

const clickedImageRect = ref(null);
const componentMounted = ref(false);
const componentReady = ref(false);
const containerRef = ref(null);
const imageGridRefs = ref(null);
const isCurrentlyLoading = ref(false);
const isInitialLoading = ref(true);
const loadMoreTrigger = ref(null);
const loadingQueue = ref([]);
const projectsBeingLoaded = ref(new Set());
const projectStore = useProjectStore();
const projectVisibleImages = ref(window.thumbnailCache?.visibleImages || {});
const savedScrollPosition = ref(0);

// Global cache for thumbnails across route changes
if (!window.thumbnailCache) {
  window.thumbnailCache = {
    visibleImages: {},
    loadingStatus: {}
  };
}

// Use the shared thumbnails hook
const {
  getProjectVisibleImages,
  projectLoadingStatus,
  pauseLoadingForRouteChange
} = useSharedThumbnails();


const loadAllThumbnails = async (project) => {
  if (!project || !project.id) return;

  // Check if we already have loaded this project's thumbnails
  if (projectVisibleImages.value[project.id]?.length > 0) {
    return;
  }

  if (!projectLoadingStatus.value[project.id]) {
    projectLoadingStatus.value[project.id] = {
      isLoading: true,
      currentIndex: 0,
      loadedImageIds: new Set()
    };
  }

  if (!projectVisibleImages.value[project.id]) {
    projectVisibleImages.value[project.id] = [];
  }

  const status = projectLoadingStatus.value[project.id];

  const allImages = getProjectAllImages(project);

  if (!allImages.length) {
    status.isLoading = false;
    return;
  }

  try {
    const loadPromises = allImages.map(image => {
      if (status.loadedImageIds.has(image.id)) return Promise.resolve();

      status.loadedImageIds.add(image.id);

      return new Promise(resolve => {
        const img = new Image();
        img.src = getImageUrl(image, false, 'main');
        img.onload = () => resolve();
        img.onerror = () => resolve();
      });
    });

    await Promise.all(loadPromises);

    projectVisibleImages.value[project.id] = allImages;
    status.isLoading = false;
    status.currentIndex = allImages.length;

    // Update the global cache
    window.thumbnailCache.visibleImages = { ...projectVisibleImages.value };
    window.thumbnailCache.loadingStatus = { ...projectLoadingStatus.value };

  } catch (error) {
    console.error('Error loading thumbnails:', error);
    status.isLoading = false;
  }
};

const getProjectAllImages = (project) => {
  if (!project?.attributes?.images?.data) return [];
  return project.attributes.images.data;
};

// Check if a project is fully ready to display
const isProjectReady = (projectId) => {
  if (!projectId) return false;

  // If component is not ready, don't show any projects yet
  if (!componentReady.value) return false;

  // If project is currently being loaded, don't show it yet
  if (projectsBeingLoaded.value.has(projectId)) return false;

  // Project is ready if it has visible images
  return (getProjectVisibleImages(projectId)?.length > 0);
};

const queueProjectForLoading = (project) => {
  if (!project || !project.id) return;

  // Check if already have cached images
  if (projectVisibleImages.value[project.id]?.length > 0) {
    return;
  }

  if (!loadingQueue.value.includes(project.id) && !projectsBeingLoaded.value.has(project.id)) {
    loadingQueue.value.push(project.id);

    if (!isCurrentlyLoading.value) {
      processLoadingQueue();
    }
  }
};

const processLoadingQueue = async () => {
  if (isCurrentlyLoading.value || loadingQueue.value.length === 0) return;

  isCurrentlyLoading.value = true;

  try {
    const projectId = loadingQueue.value[0];
    const project = projectStore.projects.find(p => p.id === projectId);

    if (project) {
      projectsBeingLoaded.value.add(project.id);
      await loadAllThumbnails(project);
      projectStore.markProjectLoaded(project.id);
      projectsBeingLoaded.value.delete(project.id);
    }

    loadingQueue.value.shift();
    isCurrentlyLoading.value = false;
    nextTick(() => processLoadingQueue());
  } catch (error) {
    console.error('Error processing loading queue:', error);

    // Make sure to clean up if error occurs
    if (loadingQueue.value[0]) {
      projectsBeingLoaded.value.delete(loadingQueue.value[0]);
    }

    loadingQueue.value.shift();
    isCurrentlyLoading.value = false;
    nextTick(() => processLoadingQueue());
  }
};

const loadImagesForVisibleProjects = () => {
  if (!projectStore.visibleProjects?.length) return;

  // Clear existing projects from the queue
  loadingQueue.value = [];

  projectStore.visibleProjects.forEach(project => {
    if (project) {
      queueProjectForLoading(project);
    }
  });

  // Start processing the queue
  nextTick(() => {
    processLoadingQueue();
    componentReady.value = true;
  });
};

// Initialize projects in store if needed
if (projectStore.projects === null && props.projects?.length > 0) {
  projectStore.projects = props.projects;

  if (projectStore.visibleProjects?.length === 0 && typeof projectStore.initVisibleProjects === 'function') {
    projectStore.initVisibleProjects();
  }
}

const watchForNewProjects = () => {
  const container = document.querySelector('.projects-container');
  if (!container) return null;

  const observer = new MutationObserver(() => {
    loadImagesForVisibleProjects();
  });

  observer.observe(container, {
    childList: true,
    subtree: true
  });

  return observer;
};

const {
  isExpanded,
  currentProjectIndex,
  currentImageIndex,
  projectClasses,
  infoClasses,
  getImageUrl,
  navigate,
  handleResize,
  handleWheel,
  handleScroll,
  handleTouchStart,
  handleTouchMove,
  handleTouchEnd,
  disableScrollAnimation,
  enableScrollAnimation
} = useGallery(null, () => {
  if (imageGridRefs.value && currentProjectIndex.value !== null) {
    const grid = Array.isArray(imageGridRefs.value)
        ? imageGridRefs.value[currentProjectIndex.value]
        : imageGridRefs.value;

    return grid?.scrollContainerRef;
  }
  return null;
});

// UNIFIED GALLERY CONTROL FUNCTIONS

const expandGallery = (pIndex, imageIndex = 0) => {
  // Save current scroll position before expanding
  savedScrollPosition.value = window.pageYOffset || document.documentElement.scrollTop;

  currentProjectIndex.value = pIndex;
  currentImageIndex.value = imageIndex;
  isExpanded.value = true;
};

const minimizeGallery = () => {
  if (!isExpanded.value) return;

  isExpanded.value = false;
  nextTick(() => {
    window.scrollTo(0, savedScrollPosition.value);
  });
};

const toggleGallery = (pIndex) => {
  if (isExpanded.value && currentProjectIndex.value === pIndex) {
    minimizeGallery();
  } else {
    expandGallery(pIndex);
  }
};

// EVENT HANDLERS

const handleHeaderClick = (pIndex) => {
  toggleGallery(pIndex);
};

const handleImageSelection = async (projectIndex, imageIndex, event) => {
  if (isExpanded.value) return;

  const project = projectStore.visibleProjects[projectIndex];
  if (!project) {
    console.error('Selected project not found at index:', projectIndex);
    return;
  }

  if (event?.target) {
    clickedImageRect.value = event.target.getBoundingClientRect();
  }

  try {
    disableScrollAnimation();

    if (!projectStore.projectsWithLoadedCriticalImages.has(project.id)) {
      await projectStore.loadCriticalImages(project, imageIndex);
    }

    expandGallery(projectIndex, imageIndex);

    nextTick(() => {
      const expandedContainer = document.querySelector('.project-row.expanded .images-scroll-container');
      if (expandedContainer) {
        const imageElements = expandedContainer.querySelectorAll('.image-wrapper');
        if (imageElements[imageIndex]) {
          const targetLeft = imageElements[imageIndex].offsetLeft;
          expandedContainer.scrollLeft = targetLeft;

          setTimeout(() => {
            expandedContainer.scrollLeft = targetLeft;
          }, 10);
        }
      }

      setTimeout(() => {
        enableScrollAnimation();
      }, 50);
    });
  } catch (error) {
    console.error('Error during image selection:', error);
    enableScrollAnimation();
  }
};

const infiniteScrollObserver = ref(null);
let projectsObserver = null;

const setupInfiniteScroll = () => {
  if (infiniteScrollObserver.value) {
    infiniteScrollObserver.value.disconnect();
  }

  nextTick(() => {
    if (!loadMoreTrigger.value) return;

    try {
      const observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && typeof projectStore.loadMoreProjects === 'function') {
          projectStore.loadMoreProjects();
        }
      }, { threshold: 0.1 });

      observer.observe(loadMoreTrigger.value);
      infiniteScrollObserver.value = observer;
    } catch (error) {
      console.error('Error setting up IntersectionObserver:', error);
    }
  });
};

watch(() => projectStore.visibleProjects.length, (newLength, oldLength) => {
  if (newLength !== oldLength) {

    nextTick(() => {
      setupInfiniteScroll();

      if (newLength > oldLength) {
        const newProjects = projectStore.visibleProjects.slice(oldLength);
        newProjects.forEach(project => {
          if (project) {
            queueProjectForLoading(project);
          }
        });
        processLoadingQueue();
      }
    });
  }
});

watch([isExpanded, currentProjectIndex], ([newExpanded, newIndex], [oldExpanded, oldIndex]) => {
  if (newExpanded && newIndex !== null && (newExpanded !== oldExpanded || newIndex !== oldIndex)) {
    const currentProject = projectStore.visibleProjects[newIndex];
    if (currentProject) {
      nextTick(() => {
        projectStore.preloadAdjacentProject('next', currentProject.id);
        projectStore.preloadAdjacentProject('previous', currentProject.id);
      });
    }
  }
});

// For Vue's keep-alive feature - called when component is activated
onActivated(() => {

  // Set component ready
  componentReady.value = false;

  // Use timeout to let DOM render first
  setTimeout(() => {
    componentReady.value = true;
    processLoadingQueue();
  }, 50);

  // Check if we need to re-init observers
  if (!projectsObserver) {
    nextTick(() => {
      projectsObserver = watchForNewProjects();
      setupInfiniteScroll();
    });
  }
});

onMounted(async () => {
  componentMounted.value = true;
  isInitialLoading.value = true;

  // Load projects if needed
  if (!projectStore.projects) {
    await projectStore.loadProjects();
  }

  if (projectStore.projects &&
      projectStore.visibleProjects?.length === 0 &&
      typeof projectStore.initVisibleProjects === 'function') {
    projectStore.initVisibleProjects();
  }

  // Loading is now complete
  isInitialLoading.value = false;

  // Start with component not ready
  componentReady.value = false;

  // Load any needed thumbnails
  nextTick(() => {
    // Queue all visible projects for loading that don't have cached thumbnails
    loadImagesForVisibleProjects();

    // Set up observers
    projectsObserver = watchForNewProjects();
    setupInfiniteScroll();

    // Mark component as ready after short delay to ensure DOM is updated
    setTimeout(() => {
      componentReady.value = true;
    }, 50);
  });

  window.addEventListener('resize', handleResize);
});

onBeforeUnmount(() => {
  pauseLoadingForRouteChange();

  window.removeEventListener('resize', handleResize);
  if (projectsObserver) {
    projectsObserver.disconnect();
    projectsObserver = null;
  }
});

onUnmounted(() => {
  if (infiniteScrollObserver.value) {
    infiniteScrollObserver.value.disconnect();
    infiniteScrollObserver.value = null;
  }

  // Save our state to the global cache
  window.thumbnailCache.visibleImages = { ...getProjectVisibleImages() };
  window.thumbnailCache.loadingStatus = { ...projectLoadingStatus.value };

  componentMounted.value = false;
});
</script>

<style lang="scss" scoped>
.projects {
  position: relative;
  display: flex;
  flex-direction: column;

  &:not(.expanded) {
    margin-top: 35vh;
  }
}

.global-spinner-container {
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  background-color: rgba(255, 255, 255, 0.9);

  .spinner {
    width: 50px;
    height: 50px;
    border-width: 4px;
  }
}

.projects-container {
  display: grid;
  position: relative;
  outline: none;
  width: 100%;
  overflow: hidden;
  gap: 60px;

  &.expanded {
    overflow: hidden;
    height: calc(100vh - 25px);
  }

  /* Target the last project-row specifically */
  & > .project-row:last-of-type {
    margin-bottom: -60px;
  }
}

.load-more-trigger {
  margin-top: -60px;
  height: 1px;
  width: 100%;
  background-color: transparent;
}


.load-more-trigger {
  margin-top: -60px;
  height: 1px;
  width: 100%;
  background-color: transparent;
}

.project-row {
  transition: all 300ms ease-in-out;
  opacity: 1;

  &.loading {
    opacity: 0;
    pointer-events: none;
  }

  &.expanded {
    overflow: hidden;
    left: 0;
    width: 100%;
    position: fixed;
    margin: 0;
    z-index: 9999;
    background: white;
    height: calc(100vh - 25px);
  }

  &.hidden {
    opacity: 0;
    pointer-events: none;
    position: absolute;
    visibility: hidden;
  }
}

.project-content {
  height: 100%;
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: visible;

  .project-row.expanded & {
    max-height: calc(100vh - 25px);
  }
}

.spinner-container {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.spinner {
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-left-color: #000;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  animation: spin 1s ease infinite;
}

.load-more-trigger {
  height: 1px;
  width: 100%;
  background-color: transparent;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>